import { useEffect, useState, Children, isValidElement, ReactNode } from 'react'
import { cn } from 'shared/styles'
import { Column, ColumnProps, PaginationProps } from './types'
import { Pagination, SortIcon } from './ui'

type Props = {
  tableStyle?: string
  data: any
  children: ReactNode
  pagination?: PaginationProps
}

export const CustomTable = ({
  tableStyle,
  data,
  children,
  pagination,
}: Props) => {
  const [sortedData, setSortedData] = useState(data)
  const [sortDirection, setSortDirection] = useState<{
    [key: string]: 'asc' | 'desc'
  }>({})

  useEffect(() => {
    setSortedData(data)
  }, [data])

  const columns: Column[] = Children.toArray(children)
    .filter(isValidElement)
    .map((child) => {
      const { columnKey, title, render, columnStyle, sorter } =
        child.props as ColumnProps

      return { key: columnKey, title, render, columnStyle, sorter }
    })

  const handleSort = (
    key: string,
    sorter: (a: string, b: string) => number,
  ) => {
    const currentDirection = sortDirection[key] || 'asc'
    const newDirection = currentDirection === 'asc' ? 'desc' : 'asc'
    const sorted = [...sortedData].sort((a, b) => {
      const result = sorter(a, b)

      return currentDirection === 'asc' ? result : -result
    })

    setSortedData(sorted)
    setSortDirection({ ...sortDirection, [key]: newDirection })
  }

  return (
    <>
      <table className={tableStyle}>
        <thead>
          <tr className='border'>
            {columns.map((column) => (
              <th
                className={cn(
                  column.sorter && 'cursor-pointer',
                  column.columnStyle,
                )}
                key={column.key}
                onClick={() =>
                  column.sorter && handleSort(column.key, column.sorter)
                }
              >
                <div
                  className={cn(
                    column.sorter && 'border-r',
                    'flex items-center gap-5',
                  )}
                >
                  {column.title}
                  {column.sorter && (
                    <SortIcon value={sortDirection[column.key]} />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row: any, rowIndex: string) => (
            <tr
              className='border hover:bg-zinc-50'
              key={rowIndex}
            >
              {columns.map((column) => (
                <td
                  className={column.columnStyle}
                  key={column.key}
                >
                  {column.render ? column.render(row) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {pagination && (
        <Pagination
          total={pagination.total}
          current={pagination.current}
          pageSize={pagination.pageSize}
          onChange={pagination.onChange}
          onShowSizeChange={pagination.onShowSizeChange}
        />
      )}
    </>
  )
}
CustomTable.Column = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  columnKey,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  title,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  render,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  columnStyle,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  sorter,
}: ColumnProps) => null
